import { isMeterPlacement, MeterPlacement } from '@enmon/commons/lib/enmon';
import { Commodity } from '@enmon/commons/lib/shared';
import { updateInventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import type { InventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import { updateInventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import {
  FLOORPLAN_PLACEMENT_STATES,
  GPS_CAPTURE_STATES,
  updateInventorizationPosition,
} from '@/features/inventorization/domain/inventorization-position';
import {
  applyInventorizationRegisterDefaults,
  type InventorizationRegisterDefault,
} from '@/features/inventorization/domain/inventorization-reading';
import { updateInventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import { createTechnicalPatchFromMeter } from '@/features/inventorization/matched-meter-technical-patch';
import type { CaptureSession } from '@/shared/capture/capture-session';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';
import type { MeterSearchResult } from '@/shared/meter-identification';

export function applyMatchedMeterToInventorizationSession(
  session: CaptureSession,
  meter: MeterSearchResult,
  updatedAt = new Date().toISOString(),
) {
  const withMeterIds = {
    ...session,
    _id: meter.data._id,
    meterId: meter.data.id,
  };
  const hasGps = typeof meter.data.gpsLat === 'number' && typeof meter.data.gpsLng === 'number';
  const hasFloorplanPosition =
    typeof meter.data.floorplanLat === 'number' && typeof meter.data.floorplanLng === 'number';
  const buildingName = String(meter.data.buildingName ?? '');
  const floorName = String(meter.data.floorName ?? '');
  const ownerName = String(meter.data.ownerName ?? '');
  const tenantName = String(meter.data.tenant ?? '');
  const withPosition = updateInventorizationPosition(
    withMeterIds,
    {
      building: buildingName,
      buildingId: meter.data.buildingId ?? null,
      floor: floorName,
      floorplanLat: meter.data.floorplanLat ?? null,
      floorplanLng: meter.data.floorplanLng ?? null,
      floorplanState: hasFloorplanPosition
        ? FLOORPLAN_PLACEMENT_STATES.placed
        : FLOORPLAN_PLACEMENT_STATES.notAvailable,
      gpsLat: meter.data.gpsLat ?? null,
      gpsLng: meter.data.gpsLng ?? null,
      gpsState: hasGps ? GPS_CAPTURE_STATES.captured : GPS_CAPTURE_STATES.unavailable,
      gpsUnavailableReason: hasGps
        ? ''
        : translateInventoryText(
            'app.inventory.position.gps.noBackendCoordinates',
            'No GPS coordinates from backend meter.',
          ),
    },
    updatedAt,
  );
  const withContext = updateInventorizationContext(
    withPosition,
    {
      buildingName,
      floorName,
      ownerName,
      tenantName,
    },
    updatedAt,
  );

  const identityPatch: Partial<InventorizationIdentity> = {
    commodity: meter.data.commodity ?? Commodity.Other,
    newMeter: meter.data.newMeter ?? false,
    placement: inferMeterPlacementFromMeter(meter),
  };

  if (meter.data.apiKey) {
    identityPatch.apiKey = meter.data.apiKey;
  }

  if (meter.data.serial) {
    identityPatch.serial = meter.data.serial;
  }

  if (meter.data.manufacturerId) {
    identityPatch.manufacturer = String(meter.data.manufacturerId);
  }

  if (meter.data.meterModelId) {
    identityPatch.meterModel = String(meter.data.meterModelId);
  }

  if (meter.data.manufactured) {
    identityPatch.manufactured = meter.data.manufactured;
  }

  if (meter.data.calibrated) {
    identityPatch.calibrated = meter.data.calibrated;
  }

  const withIdentity = updateInventorizationIdentity(withContext, identityPatch, updatedAt);
  const technicalPatch = createTechnicalPatchFromMeter(withIdentity, meter);
  const withTechnical =
    Object.keys(technicalPatch).length > 0
      ? updateInventorizationTechnicalDetails(withIdentity, technicalPatch, updatedAt)
      : withIdentity;

  return applyInventorizationRegisterDefaults(
    withTechnical,
    (meter.data.registers as InventorizationRegisterDefault[] | undefined) ?? [],
    updatedAt,
  );
}

export function inferMeterPlacementFromMeter(meter: MeterSearchResult): InventorizationIdentity['placement'] {
  const placement = readMeterPlacement(meter.data.placement);

  if (placement === MeterPlacement.Secondary) {
    return MeterPlacement.Secondary;
  }

  return MeterPlacement.Main;
}

function readMeterPlacement(value: unknown) {
  if (isMeterPlacement(value)) {
    return value;
  }

  return undefined;
}
